import type {
  AnyVariables,
  CombinedError,
  OperationContext,
  TypedDocumentNode,
} from "urql";

import type { UrqlClient } from "../../remote/createUrqlClient";
import type { LoggerType } from "../log";

import { ClientError, graphqlErrorToClientError } from "../error";

export interface AlaffiaClientModuleArgs {
  apiUrl: string;
  getGqlClient: (environment?: "tonik-preview" | "alafed") => UrqlClient;
  logger: LoggerType;
  environment?: "tonik-preview" | "alafed";
}

interface GraphQLResult<TResult, TError = CombinedError> {
  data: TResult | null;
  error: TError | null;
}

export abstract class AlaffiaClientModule {
  protected apiUrl: string;
  protected getGqlClient: (
    environment?: "tonik-preview" | "alafed",
  ) => UrqlClient;
  protected logger: LoggerType;
  protected environment: "tonik-preview" | "alafed";

  constructor(args: AlaffiaClientModuleArgs) {
    this.apiUrl = args.apiUrl;
    this.getGqlClient = args.getGqlClient;
    this.logger = args.logger;
    this.environment = args.environment ?? "tonik-preview";
  }

  protected getClientError = (contextMessage: string, error: unknown) => {
    const originalMessage =
      error instanceof Error
        ? error.message
        : (error?.toString() ?? "Unknown error");
    const msg = contextMessage + ": " + originalMessage;
    this.logger.error(msg, { error });
    return new ClientError(msg, { error });
  };

  protected runGqlQuery = async <TResult, TVariables extends AnyVariables>(
    query: TypedDocumentNode<TResult, TVariables>,
    variables: TVariables,
    _queryName: string,
    queryContext?: Partial<OperationContext>,
    environmentOverride?: "tonik-preview" | "alafed",
  ): Promise<GraphQLResult<TResult>> => {
    const client = this.getGqlClient(environmentOverride ?? this.environment);
    const result = await client.query(
      query,
      variables,
      queryContext ?? {
        requestPolicy: "network-only",
      },
    );
    if (result.error && !result.data) {
      const error = graphqlErrorToClientError(result.error);
      throw error;
    }
    return {
      data: result.data ?? null,
      error: result.error ?? null,
    };
  };

  protected runGqlMutation = async <TResult, TVariables extends AnyVariables>(
    mutation: TypedDocumentNode<TResult, TVariables>,
    variables: TVariables,
    _mutationName: string,
    mutationContext?: Partial<OperationContext>,
    environmentOverride?: "tonik-preview" | "alafed",
  ): Promise<GraphQLResult<TResult>> => {
    const client = this.getGqlClient(environmentOverride ?? this.environment);
    const result = await client.mutation(
      mutation,
      variables,
      mutationContext ?? {
        requestPolicy: "network-only",
      },
    );
    if (result.error && !result.data) {
      const error = graphqlErrorToClientError(result.error);
      throw error;
    }
    return {
      data: result.data ?? null,
      error: result.error ?? null,
    };
  };
}
